/* eslint-disable react/prop-types */
import React, { useEffect, useMemo, useState } from 'react';
import AsyncSelect from 'react-select/async';
import ReactSelect from 'react-select';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormLabel,
  TextField
} from '@mui/material';

const selectStyles = {
  menuPortal: (base) => ({ ...base, zIndex: 1400 }),
  control: (base) => ({ ...base, minHeight: 40 })
};

const dateValue = (value) => {
  if (!value) return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '' : date.toISOString().slice(0, 10);
};

const referenceOption = (reference, useLocalId = false) =>
  reference
    ? {
        value: String(useLocalId ? reference.id : reference.value),
        label: reference.name,
        reference
      }
    : null;

const ReferenceSelect = ({ label, value, options, onChange }) => {
  const selectOptions = options.map((option) => referenceOption(option));
  const selected = selectOptions.find((option) => option.value === String(value?.value || '')) || null;
  return (
    <FormControl fullWidth>
      <FormLabel>{label}</FormLabel>
      <ReactSelect
        isSearchable
        value={selected}
        options={selectOptions}
        // eslint-disable-next-line no-undef
        menuPortalTarget={document.body}
        menuPosition="fixed"
        styles={selectStyles}
        onChange={(option) => onChange(option?.reference || null)}
      />
    </FormControl>
  );
};

const PurchasePopup = ({ open, row, options, loadProductOptions, saving, onSave, onClose }) => {
  const [draft, setDraft] = useState(row);

  useEffect(() => {
    setDraft(row);
  }, [row]);

  const subcategoryOptions = useMemo(() => {
    const categoryId = draft?.category?.value || draft?.boxshallName?.category?.value;
    return options.subcategories.filter(
      (option) => !categoryId || String(option.category?.value) === String(categoryId)
    );
  }, [draft?.boxshallName, draft?.category, options.subcategories]);

  if (!draft) return null;

  const change = (name, value) => setDraft((current) => ({ ...current, [name]: value }));
  const selectedProduct = referenceOption(draft.boxshallName, true);

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="lg">
      <DialogTitle>Edit Purchase</DialogTitle>
      <DialogContent
        dividers
        sx={{
          display: 'grid',
          gap: 2,
          gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
          pt: 2
        }}
      >
        <FormControl fullWidth>
          <FormLabel>Boxshall Name</FormLabel>
          <AsyncSelect
            isSearchable
            cacheOptions
            defaultOptions
            value={selectedProduct}
            loadOptions={loadProductOptions}
            // eslint-disable-next-line no-undef
            menuPortalTarget={document.body}
            menuPosition="fixed"
            styles={selectStyles}
            onChange={(option) => {
              const product = option?.reference || null;
              setDraft((current) => ({
                ...current,
                boxshallName: product,
                boxshallDescription: product?.description || current.boxshallDescription,
                unitPrice: product?.unitPrice ?? current.unitPrice,
                category: product?.category || current.category,
                subCategory: product?.subcategory || current.subCategory
              }));
            }}
          />
        </FormControl>

        <TextField
          label="Boxshall Description"
          value={draft.boxshallDescription || ''}
          onChange={(event) => change('boxshallDescription', event.target.value)}
          fullWidth
        />
        <TextField
          label="Invoice Date"
          type="date"
          value={dateValue(draft.invoiceDate)}
          onChange={(event) => change('invoiceDate', event.target.value)}
          InputLabelProps={{ shrink: true }}
          fullWidth
        />
        <TextField
          label="Invoice No."
          value={draft.invoiceNumber || ''}
          onChange={(event) => change('invoiceNumber', event.target.value)}
          fullWidth
        />

        <ReferenceSelect
          label="Supplier"
          value={draft.supplierName}
          options={options.suppliers}
          onChange={(value) => change('supplierName', value)}
        />
        <TextField
          label="Supplier Code"
          value={draft.supplierCode || ''}
          onChange={(event) => change('supplierCode', event.target.value)}
          fullWidth
        />
        <TextField
          label="Supplier Description"
          value={draft.supplierDescription || ''}
          onChange={(event) => change('supplierDescription', event.target.value)}
          fullWidth
        />
        <TextField
          label="Qty"
          type="number"
          value={draft.qty ?? ''}
          inputProps={{ step: 'any' }}
          onChange={(event) => change('qty', event.target.value)}
          fullWidth
        />
        <TextField
          label="Unit / List Price (AUD)"
          type="number"
          value={draft.unitPrice ?? ''}
          inputProps={{ step: 'any' }}
          onChange={(event) => change('unitPrice', event.target.value)}
          fullWidth
        />

        <ReferenceSelect
          label="Category"
          value={draft.category}
          options={options.categories}
          onChange={(value) => {
            const currentSubcategory = options.subcategories.find(
              (option) => String(option.value) === String(draft.subCategory?.value || '')
            );
            setDraft((current) => ({
              ...current,
              category: value,
              subCategory:
                String(currentSubcategory?.category?.value || '') === String(value?.value || '')
                  ? current.subCategory
                  : null
            }));
          }}
        />
        <ReferenceSelect
          label="Subcategory"
          value={draft.subCategory}
          options={subcategoryOptions}
          onChange={(value) => change('subCategory', value)}
        />
        <ReferenceSelect
          label="Class"
          value={draft.class}
          options={options.classes}
          onChange={(value) => change('class', value)}
        />
        <TextField
          label="Customer Ref"
          value={draft.customerRef?.name || ''}
          InputProps={{ readOnly: true }}
          fullWidth
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button variant="contained" onClick={() => onSave(draft)} disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PurchasePopup;
